/**
 * Horizontal tab bar for sidebar content sections.
 * Displays icon-only tabs with tooltips; labels expand on hover / active.
 * When the sidebar is collapsed, tabs collapse into a single cycling control.
 */

import type { ReactNode } from 'react';
import { Tooltip } from './Tooltip';
import { useSidebar } from './Sidebar';

export interface SidebarTab {
  /** Unique tab identifier */
  id: string;
  /** Icon element to display */
  icon: ReactNode;
  /** Tooltip label */
  label: string;
  /** Optional badge count shown as a pill; renders when > 0 */
  badge?: number;
  /** Place icon before label (default) or after label */
  iconPosition?: 'start' | 'end';
}

export interface SidebarTabsProps {
  /** Available tabs */
  tabs: SidebarTab[];
  /** Currently active tab ID */
  activeTab: string;
  /** Callback when a tab is selected */
  onTabChange: (tabId: string) => void;
}

function TabIcon({ icon, badge }: { icon: ReactNode; badge?: number }) {
  return (
    <span className="sidebar-tab-icon">
      {icon}
      {badge != null && badge > 0 && (
        <span className="sidebar-tab-badge" role="status" aria-label={`${badge} new`}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </span>
  );
}

/**
 * Horizontal tab bar for switching between sidebar content sections.
 * Each tab displays an icon with a tooltip showing the label.
 */
export function SidebarTabs({ tabs, activeTab, onTabChange }: SidebarTabsProps) {
  const { isExpanded } = useSidebar();

  if (!isExpanded) {
    const activeIndex = Math.max(
      0,
      tabs.findIndex((tab) => tab.id === activeTab),
    );
    const active = tabs[activeIndex] ?? tabs[0];
    if (!active) {
      return null;
    }

    return (
      <div
        className="sidebar-tabs sidebar-tabs-collapsed"
        data-testid="sidebar-tabs"
        data-tour="sidebar-tabs"
      >
        <Tooltip content={active.label} position="right">
          <button
            type="button"
            className="sidebar-tab sidebar-tab-active"
            onClick={() => {
              const next = tabs[(activeIndex + 1) % tabs.length];
              if (next) onTabChange(next.id);
            }}
            aria-label={active.label}
            aria-pressed={true}
            data-tab-id={active.id}
            data-testid="sidebar-tabs-cycle"
          >
            <TabIcon icon={active.icon} badge={active.badge} />
          </button>
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      className="sidebar-tabs"
      data-testid="sidebar-tabs"
      data-tour="sidebar-tabs"
    >
      {tabs.map((tab) => {
        const iconPosition = tab.iconPosition ?? 'start';
        const icon = <TabIcon icon={tab.icon} badge={tab.badge} />;
        const label = <span className="sidebar-tab-label">{tab.label}</span>;

        return (
          <Tooltip key={tab.id} content={tab.label} position="bottom">
            <button
              type="button"
              className={`sidebar-tab ${activeTab === tab.id ? 'sidebar-tab-active' : ''} ${
                iconPosition === 'end' ? 'sidebar-tab-icon-end' : ''
              }`}
              onClick={() => onTabChange(tab.id)}
              aria-label={tab.label}
              aria-pressed={activeTab === tab.id}
              data-tab-id={tab.id}
            >
              {iconPosition === 'end' ? (
                <>
                  {label}
                  {icon}
                </>
              ) : (
                <>
                  {icon}
                  {label}
                </>
              )}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}
